import { useCallback, useEffect, useState } from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';

interface CurrencyTextFieldProps extends Omit<TextFieldProps, 'onChange'> {
	currency?: string;
	locale?: string;
	onChange?: (value: number) => void;
}

const CurrencyTextField = ({
	currency = 'ARS',
	locale = 'es-AR',
	value,
	onChange,
	...textFieldProps
}: CurrencyTextFieldProps) => {
	const formatCurrency = useCallback(
		(value: string) => {
			const numberValue = Number(value.replace(/[^0-9.]/g, ''));

			return new Intl.NumberFormat(locale, {
				style: 'currency',
				currency: currency,
				currencyDisplay: 'narrowSymbol',
			}).format(numberValue);
		},
		[locale, currency]
	);

	const [displayValue, setDisplayValue] = useState(value ? formatCurrency(value.toString()) : '');

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const rawValue = event.target.value;
		const sanitizedValue = rawValue.replace(/[^0-9.,]/g, '');

		const parts = sanitizedValue.split(/[.,]/);
		if (parts.length > 2) {
			const firstPart = parts[0];
			const decimalPart = parts.slice(1).join('').slice(0, 2);
			const limitedValue = decimalPart ? `${firstPart}.${decimalPart}` : firstPart;
			setDisplayValue(limitedValue);
			onChange?.(parseFloat(limitedValue));
			return;
		}

		// Limitar a 2 decimales
		if (parts[1] && parts[1].length > 2) {
			const limitedValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
			setDisplayValue(limitedValue);
			onChange?.(parseFloat(limitedValue));
			return;
		}

		// Si está vacío
		if (sanitizedValue === '') {
			setDisplayValue('');
			onChange?.(0);
			return;
		}

		// Reemplazar comas por puntos para el valor numérico
		const numericValue = parseFloat(sanitizedValue.replace(',', '.'));
		if (!isNaN(numericValue)) {
			setDisplayValue(sanitizedValue);
			onChange?.(numericValue);
		}
	};

	const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const rawValue = e.target.value;
		if (rawValue === '') {
			setDisplayValue('');
			onChange?.(0);
			return;
		}

		const sanitizedValue = rawValue.replace(/[^0-9.]/g, '');
		const numericValue = parseFloat(sanitizedValue) || 0;
		const formatted = formatCurrency(numericValue.toString());
		setDisplayValue(formatted);
	};

	const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		const input = e.target;

		setTimeout(() => {
			if (Number(input.value.replace(/[\s$]/g, '')) !== 0) {
				input.value = input.value.replaceAll(/\./g, '').replace(/\,/g, '.').replace(/[\s$]/g, '');
			}
			input.select();
		}, 0);
	};

	return (
		<TextField
			{...textFieldProps}
			value={displayValue}
			onChange={handleChange}
			onBlur={handleOnBlur}
			onFocus={handleOnFocus}
		/>
	);
};

export default CurrencyTextField;
